import { Line } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'

import { Trans } from '@/components'
import { useThemeMode } from '@/contexts/ThemeContext'
import { getChartThemeColors, getLineChartAxisTheme } from '@/utils/chart-theme'
import { formatBytes } from '@/utils/formatter'

import { bytesToSize, formatValueWithUnit, HostMonitorValue } from '../hooks/useNodeMonitorData'

// Color palettes
const PRIMARY_COLORS = ['#365FEF', '#6B8AF5', '#9FB5FA', '#D4DFFD']
const SECONDARY_COLORS = ['#FC9C28', '#FDBA6A', '#FED89C', '#FFF0D4']

export interface DualLineData {
  name: string
  primaryValues: HostMonitorValue[]
  secondaryValues: HostMonitorValue[]
  isEmpty: boolean
}

export interface DualLineChartProps {
  data: DualLineData[]
  selectedItems: string[]
  labels: { primary: string; secondary: string }
  filterDuration: number
  tooltipColorized?: boolean
}

interface MultiLineValue extends HostMonitorValue {
  series: string
}

// Render tooltip section for primary/secondary data
function renderTooltipSection(
  label: string,
  markerColor: string,
  hasAggregation: boolean,
  data: MultiLineValue,
): string {
  if (hasAggregation) {
    return `
      <div style="background: rgba(255,255,255,0.08); border-radius: 8px; padding: 4px 8px; display: flex; gap: 16px; align-items: center;">
        <div style="display: flex; gap: 8px; align-items: center; flex-shrink: 0;">
          <div style="width: 6px; height: 6px; border-radius: 50%; background: ${markerColor}; border: 2px solid ${markerColor};"></div>
          <span style="font-size: 14px; font-weight: 700; color: #ffffff;">${label}</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, auto); gap: 8px;">
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Avg</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${formatBytes(data.rawAvg || 0)}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Max</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${formatBytes(data.rawMax || 0)}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Min</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${formatBytes(data.rawMin || 0)}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Last</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${formatBytes(data.rawLast || 0)}</span>
          </div>
        </div>
      </div>`
  } else {
    return `
      <div style="background: rgba(255,255,255,0.08); border-radius: 8px; padding: 4px 8px; display: flex; gap: 16px; align-items: center;">
        <div style="display: flex; gap: 8px; align-items: center;">
          <div style="width: 6px; height: 6px; border-radius: 50%; background: ${markerColor}; border: 2px solid ${markerColor};"></div>
          <span style="font-size: 14px; font-weight: 700; color: #ffffff;">${label}</span>
        </div>
        <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${formatBytes(data.rawValue || 0)}</span>
      </div>`
  }
}

export function DualLineChart({
  data,
  selectedItems,
  labels,
  filterDuration,
  tooltipColorized = false,
}: DualLineChartProps) {
  const { themeMode } = useThemeMode()
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()

  // Filter selected items
  const selectedData = useMemo(
    () => data.filter((i) => selectedItems.includes(i.name)),
    [data, selectedItems],
  )

  const isEmpty = selectedData.length === 0 || selectedData.every((i) => i.isEmpty)

  // Calculate global max across all selected data to determine unified unit
  const globalMax = useMemo(() => {
    let max = 0
    selectedData.forEach((item) => {
      item.primaryValues.forEach((v) => {
        if (v.rawValue !== undefined && v.rawValue > max) max = v.rawValue
      })
      item.secondaryValues.forEach((v) => {
        if (v.rawValue !== undefined && v.rawValue > max) max = v.rawValue
      })
    })
    return max
  }, [selectedData])

  const unifiedUnit = useMemo(() => bytesToSize(globalMax), [globalMax])

  useEffect(() => {
    // Build chart data with series field, normalized to unified unit
    const chartData: MultiLineValue[] = []

    selectedData.forEach((item) => {
      item.primaryValues.forEach((v) => {
        chartData.push({
          ...v,
          value: formatValueWithUnit(v.rawValue || 0, unifiedUnit.unit),
          unit: unifiedUnit.unit,
          series: `${labels.primary}-${item.name}`,
        })
      })
      item.secondaryValues.forEach((v) => {
        chartData.push({
          ...v,
          value: formatValueWithUnit(v.rawValue || 0, unifiedUnit.unit),
          unit: unifiedUnit.unit,
          series: `${labels.secondary}-${item.name}`,
        })
      })
    })

    // Use unified unit for consistent display
    const unit = unifiedUnit.unit

    if (chartData.length > 0) {
      const chartTheme = getChartThemeColors()
      const primaryMarker = chartTheme.highlight
      const secondaryMarker = chartTheme.warning
      const axisTheme = getLineChartAxisTheme(themeMode, isEmpty, {
        gridColor: chartTheme.grid,
      })

      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }

      lineRef.current = new Line(id, {
        data: chartData,
        padding: 'auto',
        xField: 'time',
        yField: 'value',
        seriesField: 'series',
        color: ({ series }) => {
          if (!series) return PRIMARY_COLORS[0]
          const [type, ...nameParts] = (series as string).split('-')
          const name = nameParts.join('-') // Handle names with dashes
          const index = selectedItems.indexOf(name)
          const colorIndex = Math.max(0, index) % PRIMARY_COLORS.length
          return type === labels.primary ? PRIMARY_COLORS[colorIndex] : SECONDARY_COLORS[colorIndex]
        },
        xAxis: {
          tickCount: 7,
          label: {
            formatter: (v) => `${dayjs(v).format('HH:mm')}`,
          },
          tickLine: { length: -5 },
          line: axisTheme.axisLine,
          grid: axisTheme.xGrid,
        },
        yAxis: {
          label: {
            formatter: (v) => `${v}${unit}`,
          },
          grid: axisTheme.yGrid,
        },
        tooltip: {
          showMarkers: false,
          customContent(title, rawData) {
            if (!rawData || rawData.length === 0) return ''

            // Group by item name
            const grouped = new Map<
              string,
              { primary?: MultiLineValue; secondary?: MultiLineValue }
            >()

            rawData.forEach((item) => {
              const datum = item.data as MultiLineValue
              if (!datum?.series) return

              const [type, ...nameParts] = datum.series.split('-')
              const name = nameParts.join('-')

              if (!grouped.has(name)) {
                grouped.set(name, {})
              }
              const entry = grouped.get(name)!
              if (type === labels.primary) {
                entry.primary = datum
              } else {
                entry.secondary = datum
              }
            })

            const hasAggregation = rawData.some(
              (d) => (d.data as MultiLineValue)?.avg !== undefined,
            )

            const formattedTitle = dayjs(title).format('YYYY-MM-DD HH:mm:ss')

            // Get first interface name for header
            const firstName = grouped.keys().next().value || ''

            let content = `
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                  <span style="font-size: 12px; font-weight: 600; color: #ffffff;">${formattedTitle}</span>
                  <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">${firstName}</span>
                </div>`

            grouped.forEach((values) => {
              const { primary, secondary } = values

              // Render primary (Read/TX)
              if (primary) {
                content += renderTooltipSection(
                  labels.primary,
                  primaryMarker,
                  hasAggregation,
                  primary,
                )
              }

              // Render secondary (Write/RX)
              if (secondary) {
                content += renderTooltipSection(
                  labels.secondary,
                  secondaryMarker,
                  hasAggregation,
                  secondary,
                )
              }
            })

            content += `</div>`
            return content
          },
          domStyles: {
            'g2-tooltip': {
              backgroundColor: '#0e121b',
              color: 'white',
              borderRadius: '8px',
              padding: '8px',
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: '500',
              textAlign: 'left',
              maxHeight: '300px',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            },
          },
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: { lineWidth: 1, lineDash: [5, 3], color: chartTheme.crosshair },
            },
          },
        },
        smooth: true,
        lineStyle: { lineWidth: 1 },
        legend: false,
      })
      lineRef.current.render()
    }

    return () => {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
    }
  }, [
    id,
    selectedData,
    selectedItems,
    labels,
    isEmpty,
    filterDuration,
    tooltipColorized,
    unifiedUnit,
    themeMode,
  ])

  return (
    <div className="h-[276px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden mt-4 text-center text-tertiary t-sm absolute"
      />
      {isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <Trans ns="privateRegion" i18nKey="common.noData">
            No Data
          </Trans>
        </div>
      )}
    </div>
  )
}
